/**
 * System Config Service
 * Busca configurações do Supabase Vault (Secrets) com fallback para variáveis de ambiente
 */

import { supabase } from '../lib/supabase';

class SystemConfigService {
  private cache: Map<string, string> = new Map();
  private cacheExpiry: number = 0;
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutos
  private initialized = false;
  private initPromise: Promise<void> | null = null;

  /**
   * Inicializa o serviço carregando configs do Supabase Vault
   */
  async initialize(): Promise<void> {
    // Evitar inicializações paralelas
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this._doInitialize();
    return this.initPromise;
  }

  private async _doInitialize(): Promise<void> {
    try {
      console.log('🔧 Carregando configurações...');
      
      // Em produção, buscar da Edge Function (secrets seguras)
      const isProduction = !import.meta.env.DEV;
      
      if (isProduction) {
        try {
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
          const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
          
          const response = await fetch(`${supabaseUrl}/functions/v1/get-google-config`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${anonKey}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.clientId) this.cache.set('GOOGLE_CLIENT_ID', data.clientId);
            if (data.apiKey) this.cache.set('GOOGLE_API_KEY', data.apiKey);
            console.log('✅ Configurações carregadas da Edge Function');
          }
        } catch (e) {
          console.warn('⚠️ Edge Function não disponível');
        }
      }

      // Fallback para variáveis de ambiente (local dev)
      this.loadFromEnv();

      this.cacheExpiry = Date.now() + this.CACHE_TTL;
      this.initialized = true;
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      this.loadFromEnv();
      this.initialized = true;
    }
  }

  /**
   * Carrega configurações das variáveis de ambiente como fallback
   */
  private loadFromEnv(): void {
    const envMappings: Record<string, string> = {
      'GOOGLE_CLIENT_ID': import.meta.env.VITE_GOOGLE_CLIENT_ID || '',
      'GOOGLE_API_KEY': import.meta.env.VITE_GOOGLE_API_KEY || '',
      'OPENAI_API_KEY': import.meta.env.VITE_OPENAI_API_KEY || '',
      'ANTHROPIC_API_KEY': import.meta.env.VITE_ANTHROPIC_API_KEY || '',
      'GEMINI_API_KEY': import.meta.env.VITE_GOOGLE_API_KEY || '',
      'STRIPE_PUBLISHABLE_KEY': import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '',
    };

    Object.entries(envMappings).forEach(([key, value]) => {
      if (value) {
        this.cache.set(key, value);
      }
    });
  }

  /**
   * Verifica se o cache expirou
   */
  private isCacheExpired(): boolean {
    return Date.now() > this.cacheExpiry;
  }

  /**
   * Obtém uma configuração
   * @param key Chave da configuração
   * @param fallback Valor padrão se não encontrado
   */
  async get(key: string, fallback: string = ''): Promise<string> {
    // Inicializar se necessário
    if (!this.initialized) {
      await this.initialize();
    }

    // Recarregar se cache expirou
    if (this.isCacheExpired()) {
      this.initPromise = null;
      await this.initialize();
    }

    // Buscar do cache
    const value = this.cache.get(key);
    if (value) {
      return value;
    }

    // Fallback para variável de ambiente
    const envKey = `VITE_${key}`;
    const envValue = import.meta.env[envKey];
    if (envValue) {
      this.cache.set(key, envValue);
      return envValue;
    }

    return fallback;
  }

  /**
   * Obtém configuração de forma síncrona (usa cache, não faz fetch)
   */
  getSync(key: string, fallback: string = ''): string {
    const value = this.cache.get(key);
    if (value) return value;

    // Fallback para variável de ambiente
    const envKey = `VITE_${key}`;
    const envValue = import.meta.env[envKey];
    return envValue || fallback;
  }

  /**
   * Verifica se uma configuração está definida
   */
  async has(key: string): Promise<boolean> {
    const value = await this.get(key);
    return !!value && value.length > 0;
  }

  /**
   * Verifica se está configurado (versão síncrona)
   */
  hasSync(key: string): boolean {
    const value = this.getSync(key);
    return !!value && value.length > 0;
  }

  /**
   * Obtém múltiplas configurações de uma vez
   */
  async getMultiple(keys: string[]): Promise<Record<string, string>> {
    if (!this.initialized) {
      await this.initialize();
    }

    const result: Record<string, string> = {};
    for (const key of keys) {
      result[key] = await this.get(key);
    }
    return result;
  }

  /**
   * Configurações específicas do Google
   */
  async getGoogleConfig(): Promise<{ clientId: string; apiKey: string }> {
    const [clientId, apiKey] = await Promise.all([
      this.get('GOOGLE_CLIENT_ID'),
      this.get('GOOGLE_API_KEY')
    ]);
    return { clientId, apiKey };
  }

  /**
   * Verifica se Google está configurado
   */
  async isGoogleConfigured(): Promise<boolean> {
    const config = await this.getGoogleConfig();
    return !!(config.clientId && config.apiKey);
  }

  /**
   * Versão síncrona para verificar Google
   */
  isGoogleConfiguredSync(): boolean {
    const clientId = this.getSync('GOOGLE_CLIENT_ID');
    const apiKey = this.getSync('GOOGLE_API_KEY');
    return !!(clientId && apiKey);
  }

  /**
   * Limpa o cache forçando um reload
   */
  clearCache(): void {
    this.cache.clear();
    this.cacheExpiry = 0;
    this.initialized = false;
    this.initPromise = null;
  }

  /**
   * Atualiza uma configuração no Supabase
   */
  async update(key: string, value: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('system_config')
        .update({ value, updated_at: new Date().toISOString() })
        .eq('key', key);

      if (error) {
        console.error('Erro ao atualizar config:', error);
        return false;
      }

      // Atualizar cache local
      this.cache.set(key, value);
      return true;
    } catch (error) {
      console.error('Erro ao atualizar config:', error);
      return false;
    }
  }
}

// Instância singleton
export const systemConfigService = new SystemConfigService();

// Inicializar automaticamente
systemConfigService.initialize().catch(console.error);

